import React, { useState, useRef, useEffect, useCallback } from "react";
import { get } from "lodash";
import { IconChevronLeft, IconChevronUp } from "@tabler/icons-react";

import useMediaQuery from "@mui/material/useMediaQuery";
import { HeatMapPriceStatsData } from "../../models/heatMapModel";
import { priceStatsEndpoint } from "../../services/heatMapService";
import { fetchResponseAdapter } from "../../../../adapters/fetchAdapter";
import useCallApiAndLoad from "../../../../hooks/useCallApiAndLoad";
import ZoneHeader from "./ZoneHeader";
import ZonePriceCard from "./ZonePriceCard";
import ZoneMetrics from "./ZoneMetrics";
import ZoneTrends from "./ZoneTrends";
import ZoneActions from "./ZoneActions";
import ZoneEmptyState from "./ZoneEmptyState";

interface Props {
  polygonId: number | null;
  onClose: () => void;
  navbarHeight?: number;
}

// Contenido compartido entre mobile y web
const ZoneContent: React.FC<{
  displayed: HeatMapPriceStatsData | null;
  isOpen: boolean;
  name: string;
  avgPrice: string;
  minPrice: string;
  maxPrice: string;
  variation: string;
  capitalGain: string;
  graphData: any[];
  scrollMaxH: string;
  hideHeader?: boolean;
  onClose: () => void;
}> = ({ displayed, isOpen, name, avgPrice, minPrice, maxPrice, variation, capitalGain, graphData, scrollMaxH, hideHeader, onClose }) => (
  <>
    {!hideHeader && (
      <div className="px-4 pt-2 md:pt-4 h-[60px]">
        <ZoneHeader name={name} onClose={onClose} />
      </div>
    )}
    {displayed ? (
      <>
        <div className="flex-1 overflow-y-auto px-4" style={{ maxHeight: scrollMaxH }}>
          <ZonePriceCard avgPrice={avgPrice} minPrice={minPrice} maxPrice={maxPrice} />
          <ZoneMetrics variation={variation} capitalGain={capitalGain} />
          <ZoneTrends key={`${get(displayed, "id")}-${isOpen}`} data={graphData} />
        </div>
        <div className="px-4 py-3 min-h-[120px]">
          <ZoneActions zoneName={name} />
        </div>
      </>
    ) : (
      <ZoneEmptyState />
    )}
  </>
);

// Quita prefijos administrativos del nombre para la pestaña mobile
const stripPrefix = (name: string) =>
  name.replace(/^(Departamento|Provincia|Estado|Municipio|Distrito|Cantón|Corregimiento|Región)\s+de\s+/i, "");

const ZoneDrawer: React.FC<Props> = ({ polygonId, onClose, navbarHeight = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<HeatMapPriceStatsData | null>(null);
  const { callEndpoint } = useCallApiAndLoad();
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Retiene los últimos stats para mostrar contenido durante la animación de cierre
  const lastStats = useRef<HeatMapPriceStatsData | null>(null);
  const touchStartY = useRef(0);
  const dragOffset = useRef(0);
  const drawerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  useEffect(() => {
    if (stats) lastStats.current = stats;
  }, [stats]);

  // Fade del nombre en la pestaña al cambiar de polígono
  const [tabNameVisible, setTabNameVisible] = useState(true);

  // Bounce sutil: mueve el drawer 8px arriba y lo regresa
  const triggerBounce = useCallback(() => {
    const el = drawerRef.current;
    if (!el) return;
    el.style.transition = "transform 150ms ease-out";
    el.style.transform = "translateY(calc(100% - 36px))"; // 28 + 8px extra
    const reset = setTimeout(() => {
      el.style.transition = "transform 200ms ease-in";
      el.style.transform = "translateY(calc(100% - 28px))";
    }, 150);
    return () => clearTimeout(reset);
  }, []);

  // Abre y carga price-stats cuando hay polígono; cierra cuando no hay
  useEffect(() => {
    if (!polygonId) {
      setIsOpen(false);
      return;
    }
    if (isMobile) {
      const cleanup = triggerBounce();
      // Fade out → cambio → fade in del nombre
      setTabNameVisible(false);
      setTimeout(() => setTabNameVisible(true), 200);
      const fetchStats = async () => {
        const response = await callEndpoint(priceStatsEndpoint(polygonId));
        const adapted = fetchResponseAdapter(get(response, "data"));
        if (get(adapted, "code") === 0) setStats(get(adapted, "data"));
      };
      fetchStats();
      return cleanup;
    }
    setIsOpen(true);
    const fetchStats = async () => {
      const response = await callEndpoint(priceStatsEndpoint(polygonId));
      const adapted = fetchResponseAdapter(get(response, "data"));
      if (get(adapted, "code") === 0) setStats(get(adapted, "data"));
    };
    fetchStats();
  }, [polygonId]);

  const displayed = stats ?? lastStats.current;
  const name = get(displayed, "name", "");
  const avgPrice = get(stats, "ppm2", "$0");
  const minPrice = get(stats, "typical_range.min", "$0");
  const maxPrice = get(stats, "typical_range.max", "$0");
  const variation = get(stats, "variation", "—");
  const capitalGain = get(stats, "variation_status", "—");
  const graphData = get(stats, "capital_gains_graph", []);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const contentProps = {
    displayed, isOpen, name, avgPrice, minPrice, maxPrice,
    variation, capitalGain, graphData, onClose: handleClose,
  };

  // Drag handlers para la pestaña mobile
  const onTabTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
    dragOffset.current = 0;
    if (drawerRef.current) drawerRef.current.style.transition = "none";
  }, []);

  const tabRef = useRef<HTMLDivElement>(null);

  const onTabTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = touchStartY.current - e.touches[0].clientY;
    if (delta > 5) isDragging.current = true;
    if (!isDragging.current || !drawerRef.current) return;
    const maxDrag = window.innerHeight * 0.85;
    dragOffset.current = Math.max(0, Math.min(delta, maxDrag));
    drawerRef.current.style.transform = `translateY(calc(100% - 28px - ${dragOffset.current}px))`;
    // Fade out la pestaña cuando pasa del 50%
    if (tabRef.current) tabRef.current.style.opacity = String(Math.max(0, 1 - (dragOffset.current / (maxDrag * 0.5))));
  }, []);

  const onTabTouchEnd = useCallback(() => {
    if (drawerRef.current) drawerRef.current.style.transition = "";
    if (tabRef.current) tabRef.current.style.opacity = "";
    if (isDragging.current && dragOffset.current > 60) {
      setIsOpen(true);
    } else if (!isDragging.current) {
      setIsOpen(true); // tap
    }
    isDragging.current = false;
    dragOffset.current = 0;
  }, []);

  // Swipe down en el drawer abierto (drag handle + contenido)
  const onDrawerTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
    dragOffset.current = 0;
  }, []);

  const onDrawerTouchMove = useCallback((e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 5) isDragging.current = true;
    if (!isDragging.current || !drawerRef.current || delta < 0) return;
    dragOffset.current = delta;
    drawerRef.current.style.transition = "none";
    drawerRef.current.style.transform = `translateY(${delta}px)`;
  }, []);

  const onDrawerTouchEnd = useCallback(() => {
    if (drawerRef.current) {
      drawerRef.current.style.transition = "";
      drawerRef.current.style.transform = "";
    }
    if (isDragging.current && dragOffset.current > 80) {
      handleClose();
    }
    isDragging.current = false;
    dragOffset.current = 0;
  }, [handleClose]);

  // Mobile: drawer custom con pestaña integrada
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        <div
          onClick={handleClose}
          className={`fixed inset-0 z-30 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        />

        {/* Drawer + pestaña como un solo bloque */}
        <div
          ref={drawerRef}
          className={`fixed z-40 bottom-0 left-0 right-0 transition-transform duration-300 ease-out ${displayed ? "" : "translate-y-full"}`}
          style={{ transform: displayed ? (isOpen ? "translateY(0)" : "translateY(calc(100% - 28px))") : undefined }}
        >
          {/* Pestaña — se oculta cuando el drawer está abierto */}
          <div ref={tabRef} className={`flex justify-center pointer-events-none transition-opacity duration-300 ${isOpen ? "opacity-0" : "opacity-100"}`}>
            <div
              onTouchStart={onTabTouchStart}
              onTouchMove={onTabTouchMove}
              onTouchEnd={onTabTouchEnd}
              onClick={() => isOpen ? handleClose() : setIsOpen(true)}
              className="pointer-events-auto bg-white shadow-lg rounded-t-xl h-7 w-[200px] flex items-center cursor-pointer"
            >
              <IconChevronUp size={14} className={`text-gray-500 ml-2 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              <span className={`text-xs font-medium text-gray-700 truncate flex-1 text-center pr-6 transition-opacity duration-200 ${tabNameVisible ? "opacity-100" : "opacity-0"}`}>{stripPrefix(name)}</span>
            </div>
          </div>

          {/* Contenido del drawer */}
          <div className="bg-white rounded-t-2xl relative z-10" style={{ maxHeight: "85dvh" }}>
            {/* Zona de swipe para cerrar: drag handle + header */}
            <div
              onTouchStart={onDrawerTouchStart}
              onTouchMove={onDrawerTouchMove}
              onTouchEnd={onDrawerTouchEnd}
            >
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>
              <div className="px-4 pt-2 h-[60px] md:hidden">
                <ZoneHeader name={name} onClose={handleClose} />
              </div>
            </div>
            <ZoneContent {...contentProps} hideHeader scrollMaxH="calc(85dvh - 240px)" />
          </div>
        </div>
      </>
    );
  }

  // Web: drawer lateral derecho custom
  return (
    <div
      className={`dynamic fixed z-50 transition-transform duration-300 ease-in-out pointer-events-auto
        right-0 max-w-[400px] w-full
        ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      style={{
        top: navbarHeight + 52,
        ["--drawer-h" as string]: `calc(100dvh - ${navbarHeight + 52}px)`,
        maxHeight: `calc(100dvh - ${navbarHeight + 52}px)`,
      }}
    >
      {/* Pestaña para abrir/cerrar */}
      <button
        onClick={() => setIsOpen(true)}
        className={`absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 bg-white shadow-lg rounded-l-xl px-1.5 py-4 transition-opacity duration-300 ${isOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        <IconChevronLeft size={20} className="text-gray-600" />
      </button>

      <div className="bg-white shadow-2xl rounded-l-2xl">
        <ZoneContent {...contentProps} scrollMaxH={`calc(100dvh - ${navbarHeight + 52}px - 252px)`} />
      </div>
    </div>
  );
};

export default ZoneDrawer;
